import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'

import { PaymentPlan } from 'types'
import { useGetQuoteV2Query } from 'app/services'
import { useQuery } from 'utils'

interface PaymentPlanContextType {
  paymentPlan: PaymentPlan
  setPaymentPlan: (plan: PaymentPlan) => void
}

const PaymentPlanContext = createContext<PaymentPlanContextType | undefined>(undefined)

const PAYMENT_PLAN_STORAGE_KEY = 'paymentPlan'
const DEFAULT_PLAN = PaymentPlan.MONTHLY

interface PaymentPlanProviderProps {
  children: ReactNode
}

export const PaymentPlanProvider = ({ children }: PaymentPlanProviderProps) => {
  const query = useQuery()
  const [paymentPlan, setPaymentPlanState] = useState<PaymentPlan>(DEFAULT_PLAN)

  useEffect(() => {
    const queryPlan = query.get('payment') as PaymentPlan | null

    if (queryPlan) {
      setPaymentPlanState(queryPlan)
      localStorage.setItem(PAYMENT_PLAN_STORAGE_KEY, queryPlan)
    } else {
      const storedPlan = localStorage
        .getItem(PAYMENT_PLAN_STORAGE_KEY)
        ?.toLowerCase() as PaymentPlan | null
      setPaymentPlanState(storedPlan || DEFAULT_PLAN)
    }
  }, [location.search])

  const setPaymentPlan = (newPlan: PaymentPlan) => {
    setPaymentPlanState(newPlan)
    localStorage.setItem(PAYMENT_PLAN_STORAGE_KEY, newPlan)
  }

  return (
    <PaymentPlanContext.Provider value={{ paymentPlan, setPaymentPlan }}>
      {children}
    </PaymentPlanContext.Provider>
  )
}

interface UsePaymentPlanOptions {
  quoteId?: string
  considerPaymentPlanIntention?: boolean
}

export const usePaymentPlan = (options?: UsePaymentPlanOptions) => {
  const context = useContext(PaymentPlanContext)

  if (!context) {
    throw new Error('usePaymentPlan must be used within a PaymentPlanProvider')
  }

  const { paymentPlan, setPaymentPlan } = context
  const paymentPlanHasBeenUpdatedRef = useRef(false)

  const { quoteId, considerPaymentPlanIntention } = options || {}

  const { data: quoteData, isSuccess } = useGetQuoteV2Query(
    { quoteId: quoteId! },
    { skip: !quoteId || !considerPaymentPlanIntention },
  )

  useEffect(() => {
    if (
      considerPaymentPlanIntention &&
      isSuccess &&
      quoteData?.paymentTypeIntention &&
      !paymentPlanHasBeenUpdatedRef.current
    ) {
      const paymentType = quoteData.paymentTypeIntention.toLowerCase() as PaymentPlan
      setPaymentPlan(paymentType)
      paymentPlanHasBeenUpdatedRef.current = true
    }
  }, [considerPaymentPlanIntention, isSuccess, quoteData, setPaymentPlan])

  return { paymentPlan, setPaymentPlan }
}
